import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { sizeAliasApi, sizeSchemaApi } from '../../services/api';

export default function SizeAliasesDashboard() {
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAlias, setEditingAlias] = useState(null);
  const [formData, setFormData] = useState({ from_size: '', to_size: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [impact, setImpact] = useState(null); // {affected_rows, sample_size_names} | null
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadAliases = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await sizeAliasApi.list();
      setAliases(data.aliases || []);
    } catch (err) {
      setError(err.message || 'Failed to load aliases');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAliases();
  }, [loadAliases]);

  const handleOpenAdd = () => {
    setEditingAlias(null);
    setFormData({ from_size: '', to_size: '', notes: '' });
    setImpact(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (alias) => {
    setEditingAlias(alias);
    setFormData({
      from_size: alias.from_size,
      to_size: alias.to_size,
      notes: alias.notes || '',
    });
    setImpact(null);
    setDialogOpen(true);
  };

  // Debounced impact preview when the user types in from_size.
  useEffect(() => {
    if (!dialogOpen || !formData.from_size) {
      setImpact(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const result = await sizeAliasApi.previewImpact(formData.from_size);
        if (!cancelled) setImpact(result);
      } catch (e) {
        if (!cancelled) setImpact(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [dialogOpen, formData.from_size]);

  const triggerReclassify = async () => {
    try {
      const result = await sizeSchemaApi.reclassify();
      const stats = result.result?.stats || {};
      setInfo(`Reclassified ${(stats.rows_processed || 0).toLocaleString()} rows in ${Math.round(stats.duration_seconds || 0)}s`);
    } catch (e) {
      setError(`Saved, but reclassify failed: ${e.message || e}`);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setInfo(null);
      if (editingAlias) {
        await sizeAliasApi.update(editingAlias.id, formData);
      } else {
        await sizeAliasApi.create(formData.from_size, formData.to_size, formData.notes);
      }
      setDialogOpen(false);
      await loadAliases();
      // Auto-reclassify so the change is visible immediately, matching the
      // "apply pending mappings" pattern from other normalization steps.
      await triggerReclassify();
    } catch (err) {
      setError(err.message || 'Failed to save alias');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (alias) => {
    try {
      setError(null);
      setInfo(null);
      await sizeAliasApi.update(alias.id, { active: !alias.active });
      await loadAliases();
      await triggerReclassify();
    } catch (err) {
      setError(err.message || 'Failed to toggle alias');
    }
  };

  // Group aliases by to_size for visual clarity
  const grouped = {};
  aliases.forEach((alias) => {
    const key = alias.to_size;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(alias);
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5">Size Aliases</Typography>
          <Typography variant="body2" color="text.secondary">
            Maps synonymous size labels to canonical forms for cross-retailer matching.
            Changes take effect on the next parser run.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
          Add Alias
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>From Size</strong></TableCell>
              <TableCell><strong>Canonical (To)</strong></TableCell>
              <TableCell><strong>Notes</strong></TableCell>
              <TableCell align="center"><strong>Active</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {aliases.map((alias) => (
              <TableRow key={alias.id} sx={{ opacity: alias.active ? 1 : 0.5 }}>
                <TableCell>
                  <Chip label={alias.from_size} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip label={alias.to_size} size="small" color="primary" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {alias.notes || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Switch
                    checked={alias.active}
                    onChange={() => handleToggleActive(alias)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenEdit(alias)}
                  >
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {aliases.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No aliases configured yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        {aliases.length} aliases ({aliases.filter(a => a.active).length} active)
      </Typography>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAlias ? 'Edit Alias' : 'Add Alias'}</DialogTitle>
        <DialogContent>
          <TextField
            label="From Size"
            value={formData.from_size}
            onChange={(e) => setFormData({ ...formData, from_size: e.target.value })}
            fullWidth
            margin="normal"
            disabled={!!editingAlias}
            helperText="The size label to match (e.g. 2XL, ONESIZE, XXXL)"
          />
          <TextField
            label="To Size (Canonical)"
            value={formData.to_size}
            onChange={(e) => setFormData({ ...formData, to_size: e.target.value })}
            fullWidth
            margin="normal"
            helperText="The canonical form to normalize to (e.g. XXL, one size, 3XL)"
          />
          <TextField
            label="Notes"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            rows={2}
          />
          {impact && impact.affected_rows > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>{impact.affected_rows.toLocaleString()}</strong> existing rows will be
              re-mapped after save (reclassification runs automatically).
              {impact.sample_size_names?.length > 0 && (
                <> Matches raw_size: {impact.sample_size_names.map(s => `"${s}"`).join(', ')}.</>
              )}
            </Alert>
          )}
          {impact && impact.affected_rows === 0 && formData.from_size && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No existing rows match raw_size <strong>"{formData.from_size}"</strong>. The alias
              will still be saved and applied to future data.
            </Alert>
          )}
          {previewLoading && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Calculating impact...
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !formData.from_size || !formData.to_size}
          >
            {saving ? 'Saving + reclassifying...' : 'Save & Apply'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
